/* eslint-disable prefer-destructuring */

import { formatAddressLabels } from '@gameday/common/formats/address'
import { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import CountrySelect from '../CountrySelect'
import DropDown from '../FormElement/DropDown'
import InputField from '../InputField'
import EntryModeSelect from './EntryModeSelect'

const StyledEntryModeWrap = styled.div`
  width: 100%;
`

const StyledFieldWrap = styled.div`
  width: ${props => (props.fieldWidth ? props.fieldWidth : '100%')};
`

const StyledAddressFields = styled.div`
  display: flex;
  flex-flow: row wrap;
  width: 100%;

  ${props =>
    props.groupAddressFields &&
    css`
      width: calc(100% + 10px);
      margin: 0 -5px;

      ${StyledFieldWrap} {
        padding: 0 5px 10px;

        .content-wrapper {
          padding: 0 !important;
        }
      }

      ${StyledEntryModeWrap} {
        padding: 0 5px;
      }
    `}
`

function AddressFields(props) {
  const {
    handleOnChange,
    addressDefinition,
    addressValue,
    subDivisions,
    groupAddressFields,
    mainInputProps,
    inputProps,
    showEntryModeSelect,
    setEntryMode,
    name,
    id,
    disabled,
    dataTestId,
    hierarchyCountryCode,
  } = props

  const [labelProps, setLabelProps] = useState({})

  useEffect(() => {
    // biome-ignore lint/complexity/useOptionalChain: TODO
    const country = addressDefinition && addressDefinition.country ? addressDefinition.country.value : ''
    const { labels } = formatAddressLabels(country)
    setLabelProps(labels)
  }, [])

  const handleAddressFieldChange = (fieldKey, fieldValue) => {
    const { labels } = formatAddressLabels(fieldValue)
    setLabelProps(labels)
    handleOnChange(
      {
        newAddress: {
          [fieldKey]: fieldValue,
        },
      },
      fieldKey
    )
  }

  const getAddressInputProps = (fieldKey, options) => {
    // biome-ignore lint/complexity/useOptionalChain: TODO
    const valueProp = (options && options.valueProp) || 'value'
    let value = null
    let label = null
    let placeholder = null
    let errorText = null
    let isRequired = false
    let defaultValue = null
    let fieldInputProps = null

    if (addressValue) {
      value = addressValue[fieldKey]
      // biome-ignore lint/complexity/useOptionalChain: TODO
    } else if (addressDefinition && addressDefinition[fieldKey]) {
      value = addressDefinition[fieldKey].value
    }

    // biome-ignore lint/complexity/useOptionalChain: TODO
    const addressField = addressDefinition && addressDefinition[fieldKey]
    if (addressField) {
      // biome-ignore lint/complexity/useOptionalChain: TODO
      label = addressField.label || (options && options.label) || null
      // biome-ignore lint/complexity/useOptionalChain: TODO
      placeholder = (options && options.placeholder) || addressField.placeholder || null
      isRequired = addressField.isRequired || (fieldKey !== 'street2' && options && options.isRequired) || false
      errorText = addressField.errorText || null
      // biome-ignore lint/complexity/useOptionalChain: TODO
      defaultValue = (options && options.defaultValue) || addressField.defaultValue || null
      fieldInputProps = addressField.fieldInputProps || null
    }

    return {
      ...inputProps,
      [valueProp]: value,
      label,
      placeholder,
      errorText,
      isRequired,
      ...(defaultValue ? { defaultValue } : {}),
      ...(fieldInputProps || {}),
      ...(groupAddressFields
        ? {
            label: null,
            customMargin: 0,
            customWidth: null,
            fullWidth: true,
            customCol: 12,
          }
        : {}),
    }
  }

  const hasStreet2Field = addressDefinition && !!addressDefinition.street2

  const addressFields = (
    <StyledAddressFields groupAddressFields={groupAddressFields} data-testid={dataTestId}>
      {showEntryModeSelect && groupAddressFields && (
        <StyledEntryModeWrap>
          <EntryModeSelect
            entryMode="manual"
            setEntryMode={setEntryMode}
            groupAddressFields={groupAddressFields}
            id={id}
            disabled={disabled}
          />
        </StyledEntryModeWrap>
      )}
      <StyledFieldWrap>
        <CountrySelect
          {...getAddressInputProps('country', { label: 'Country', placeholder: 'Select Country...' })}
          countryLabelType="label"
          name={name}
          withFlags={false}
          isClearable
          onChange={data => {
            // biome-ignore lint/complexity/useOptionalChain: TODO
            handleAddressFieldChange('country', data && data.selectedOption ? data.selectedOption.value : '')
          }}
          id={id}
          disabled={disabled}
          dataTestId="countrySelect"
          hierarchyCountryCode={hierarchyCountryCode}
        />
      </StyledFieldWrap>
      <StyledFieldWrap>
        <InputField
          name={name}
          {...getAddressInputProps('street', { label: 'Street Address Line 1', placeholder: 'Street Address Line 1' })}
          onChange={event => handleAddressFieldChange('street', event.target.value)}
          id={`${id}-street`}
          disabled={disabled}
          dataTestId="streetField"
        />
      </StyledFieldWrap>
      {hasStreet2Field && (
        <StyledFieldWrap fieldWidth={groupAddressFields && '60%'}>
          <InputField
            name={name}
            {...getAddressInputProps('street2', {
              label: 'Street Address Line 2',
              placeholder: 'Street Address Line 2',
            })}
            onChange={event => handleAddressFieldChange('street2', event.target.value)}
            id={`${id}-street2`}
            disabled={disabled}
            dataTestId="street2Field"
          />
        </StyledFieldWrap>
      )}
      <StyledFieldWrap fieldWidth={groupAddressFields && hasStreet2Field && '40%'}>
        <InputField
          name={name}
          {...getAddressInputProps('suburb', labelProps.suburb)}
          onChange={event => handleAddressFieldChange('suburb', event.target.value)}
          id={`${id}-suburb`}
          disabled={disabled}
          dataTestId="suburbField"
        />
      </StyledFieldWrap>
      <StyledFieldWrap fieldWidth={groupAddressFields && '70%'}>
        <DropDown
          name={name}
          {...getAddressInputProps('state', labelProps.state)}
          defaultOption
          filterData={[
            {
              key: 'State',
              value: subDivisions,
              key_prefix: 'count',
            },
          ]}
          disabled={disabled || (subDivisions && subDivisions.length === 0)}
          onChange={data => handleAddressFieldChange('state', data)}
          id={`${id}-state`}
          data-testid="subdivisionField"
        />
      </StyledFieldWrap>
      <StyledFieldWrap fieldWidth={groupAddressFields && '30%'}>
        <InputField
          name={name}
          {...getAddressInputProps('postalCode', labelProps.postalCode)}
          disabled={disabled || (subDivisions && subDivisions.length === 0)}
          onChange={event => handleAddressFieldChange('postalCode', event.target.value)}
          id={`${id}-postal-code`}
          dataTestId="postCodeField"
        />
      </StyledFieldWrap>
    </StyledAddressFields>
  )

  return groupAddressFields ? (
    <InputField {...mainInputProps} disabled={disabled}>
      {addressFields}
    </InputField>
  ) : (
    addressFields
  )
}

export default AddressFields
